import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Mail, Phone, MapPin, ShieldCheck, Calendar, Lock, CheckCircle2, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ClientIntakeForm from '@/src/components/ClientIntakeForm';
import ContactForm from '@/src/components/ContactForm';
import { useLocation } from 'react-router-dom';
import SEO from '@/src/components/SEO';
import { localBusinessSchema } from '@/src/lib/schema';

export default function Contact() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const service = searchParams.get('service');
  const product = searchParams.get('product');

  return (
    <div className="flex flex-col">
      <SEO 
        title="Contact AGRANDINTL – Secure Security Consulting Inquiries"
        description="Connect with AGRANDINTL for expert cybersecurity support. Book a free consultation or risk audit for your business in Washington DC, Virginia, or Maryland."
        keywords="contact cybersecurity consultant, book security audit, cybersecurity DC contact, virginia security consulting, maryland security experts"
        schema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": localBusinessSchema
        }}
      />
      {/* Page Header */}
      <section className="relative min-h-[35vh] flex flex-col justify-center overflow-hidden pt-24 pb-12 bg-secondary/[0.01]">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em] mb-8"
            >
              <Lock className="w-3 h-3" /> Secure Communication Channel
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-5xl lg:text-6xl font-display font-bold mb-8 tracking-tight leading-tight"
            >
              Consult an <br />
              <span className="text-accent">Expert</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed font-normal max-w-2xl"
            >
              Serving businesses across Washington DC, Virginia, and Maryland. Your information is handled securely and confidentially.
            </motion.p>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Multi-Step Form */}
            <div className="space-y-8">
              <div className="space-y-3">
                <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">
                  {service === 'audit' ? "Request Your " : "Start Your "}
                  <span className="text-accent">{service === 'audit' ? "Security Audit" : "Risk Review"}</span>
                </h2>
                <p className="text-sm text-muted-foreground font-normal leading-relaxed max-w-xl">
                  {product === 'complytics' 
                    ? "Request a demo of Complytics and see how AI can transform your compliance visibility."
                    : "Complete the steps below to receive a custom risk profile and strategic recommendations."}
                </p>
              </div>
              <div className="glass-card p-6 md:p-8 rounded-2xl border-white/5 bg-secondary/[0.01]">
                <ContactForm />
              </div>

              {/* What Happens Next Section */}
              <div className="space-y-6 pt-8">
                <div className="space-y-2">
                  <h3 className="text-xl font-display font-bold tracking-tight">What Happens <span className="text-accent">Next</span></h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Our process is designed to be simple, transparent, and reassuring. Here is how we initiate your security journey.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { step: "01", title: "Review", desc: "We review your request and business details to prepare for our talk." },
                    { step: "02", title: "Schedule", desc: "We schedule a short consultation to discuss your specific needs." },
                    { step: "03", title: "Assessment", desc: "We assess your current security posture with a deep-dive review." },
                    { step: "04", title: "Strategy", desc: "You receive clear recommendations and practical next steps." }
                  ].map((item, i) => (
                    <div key={i} className="p-4 rounded-xl border border-white/5 bg-white/[0.01] space-y-2 group hover:border-accent/20 transition-all">
                      <div className="text-[10px] font-bold text-accent tracking-widest">{item.step}</div>
                      <h4 className="text-sm font-bold tracking-tight group-hover:text-accent transition-colors">{item.title}</h4>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground italic text-center font-medium pt-2">
                  No pressure. Just practical guidance tailored to your business.
                </p>
              </div>
            </div>

            {/* Contact Info & Booking */}
            <div className="space-y-10">
              <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.1 }
                  }
                }}
                className="space-y-8"
              >
                <motion.h2 
                  variants={{
                    hidden: { opacity: 0, x: 10 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  className="text-xl md:text-3xl font-display font-bold tracking-tight leading-tight"
                >
                  Contact <br /> <span className="text-accent">Information</span>
                </motion.h2>
                <div className="space-y-5">
                  {[
                    { icon: Mail, label: "Email Us", value: "contact@agrandintl.com", href: "mailto:contact@agrandintl.com" },
                    { icon: Phone, label: "Call Us", value: "+1 202 630 6862", href: "tel:+12026306862" },
                    { icon: MapPin, label: "Location", value: "Washington DC, Virginia, Maryland", href: null },
                    { icon: Linkedin, label: "LinkedIn", value: "Review Professional Profile & Insights", href: "https://www.linkedin.com/in/fabiyan-anik" }
                  ].map((item, i) => (
                    <motion.div 
                      key={i} 
                      variants={{
                        hidden: { opacity: 0, x: 10 },
                        visible: { opacity: 1, x: 0 }
                      }}
                      className="flex items-start gap-4 group cursor-default"
                    >
                      <div className="w-9 h-9 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300 shrink-0">
                        <item.icon className="w-4.5 h-4.5" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-[8px] font-bold font-display uppercase tracking-[0.3em] text-muted-foreground group-hover:text-accent transition-colors">{item.label}</p>
                        {item.href ? (
                          <a 
                            href={item.href} 
                            target={item.href.startsWith('http') ? "_blank" : undefined}
                            rel={item.href.startsWith('http') ? "noopener noreferrer" : undefined}
                            className="text-base md:text-lg font-display font-bold tracking-tight hover:text-accent transition-all duration-300"
                            title={item.href.startsWith('http') ? "View professional profile" : undefined}
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-base md:text-lg font-display font-bold tracking-tight">{item.value}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="glass-card rounded-2xl p-6 md:p-8 border-accent/10 bg-accent/[0.01] space-y-4 relative overflow-hidden group"
              >
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center text-background">
                    <ShieldCheck className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="text-lg font-display font-bold tracking-tight">Selective Engagement</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed font-normal relative z-10">
                  Agrandintl works with a limited number of clients to ensure highest-level attention. All requests are professionally reviewed.
                </p>
                <div className="relative z-10 pt-4 border-t border-accent/10">
                  <p className="text-[10px] text-accent uppercase tracking-[0.2em] font-bold">
                    Next Step: Submit the intake form
                  </p>
                  <p className="text-[9px] text-muted-foreground mt-2 leading-relaxed">
                    Once submitted, our AI-driven risk system will analyze your business profile and alert our team.
                  </p>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-5 border border-white/5 rounded-2xl space-y-3 bg-secondary/[0.01] group hover:border-accent/20 transition-all duration-300"
              >
                <h4 className="font-bold font-display flex items-center gap-3 text-base tracking-tight">
                  <ShieldCheck className="w-4.5 h-4.5 text-accent" /> Secure Communication
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed font-normal">
                  All communications are handled with the highest level of confidentiality. Your information is secure within our hardened infrastructure.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
